import { AriaDatapoint } from "./AriaDatapoint";
import type { PerformanceDatapointType, DataSource, CustomWaterfallRange, PredefinedCheckpoint, PerformanceDatapointObjectType, EndProfileFunction, DatapointOptionsForPerformanceDatapoint, CustomDataType, ErrorType, TraceErrorObject, INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, } from "owa-analytics-types";
import { DatapointStatus } from "owa-analytics-types";
import type { AnalyticsCoreEventNames, AnalyticsEventNames } from "owa-analytics-events";
import type { ErrorCodes } from "owa-error-codes";
import type { MailboxInfo } from "owa-client-types";

// Inline anonymous mailbox for infrastructure telemetry - cannot import owa-mailbox-info
// or owa-account-source-list-store as it would pull them into the analytics worker build
const anonymousMailbox: MailboxInfo = {
    type: "UserMailbox",
    userIdentity: "",
    mailboxSmtpAddress: "",
};

import { hasUserWindowHadFocusSince } from "owa-analytics-utils/lib/visibilityState";
import { addStackTraceToEvent } from "owa-analytics-utils/lib/addStackTraceToEvent";
import { lazyLogPerformanceDatapoint } from "../lazyFunctions";
import { trace } from "owa-trace";
import { safeRequestAnimationFrame } from "owa-performance";
import type { PerformanceWithMemory } from "owa-performance-types";
import type { LazyLoadedModules } from "owa-bundling-light";
import { getGlobalImportStartTime, getLazyLoadedModulesAndCleanup } from "owa-bundling-light";
import { getUniquePropertyString } from "owa-analytics-core/lib/utils/getUniquePropertyString";
import { getAnalyticsFlightsAndAppSettings } from "owa-analytics-core/lib/settings/getAnalyticsFlightsAndAppSettings";
import { tryGetTraceObjectErrorFromApolloError } from "owa-trace/lib/tryGetTraceObjectErrorFromApolloError";
import { addErrorToDatapoint } from "../utils/addErrorToDatapoint";
import getDatapointStatus from "../utils/getDatapointStatus";
import type { StatusAndType } from "../utils/getDatapointStatus";
import { isNetworkDataSource } from "../utils/isNetworkDataSource";
import getUnhandledErrorCode from "../utils/getUnhandledErrorCode";
import { isFetchApiResponse } from "../utils/isFetchApiResponse";
import { isSingleResponseMessage } from "../utils/isSingleResponseMessage";
import type { ErrorCodeAndStatus } from "../utils/getUnhandledErrorCode";
import { logUsage } from "../api/logUsage";
import { scrubForPii, getSessionStage, getDataWorkerState, getOfflineFaultOrDisabledState, getOfflineFaultState, } from "owa-config";
import { logGreyError } from "../api/logGreyError";
import { addPerfomanceMarkToDatapoint } from "../utils/performanceMarks";
import { getCustomColumn, getCustomValue, getWaterfallColumnName, WATERFALL_COLUMN_PREFIX, } from "../utils/getWaterfallColumnName";
import { getIsSigninRequiredForTelemetry, getNextPaint } from "owa-analytics-shared";
import { startSelfProfiler } from "../utils/startSelfProfiler";
import { getAnalyticsAddon } from "owa-analytics-addons";
import { addEncounteredErrorCode, validateErrorCode } from "owa-error-codes";

// Lazy Module Stats for Health Data
// d: duration in ms
// a: attempts
type LazyModuleStats = {
    d: number;
    a?: number;
};

// Health data
// rc: Number of reactions scheduled for mobx
// ls: LayoutScore
// lr: Number of long running tasks
// md: Memory difference between the start and end of the datapoint
// ms: Memory at the start of the datapoint
// lm: Lazy Modules loaded during the datapoint execution
// c: number of cores
// loaf: long animation frames sources that occur within the initial event processing
// loafss: the script sources for the loaf property above
// idloaf: long animation frames sources that occur within the input delay
// idloafss: the script sources for the idloaf property above
// inp: Interaction to next paint
// id: Input delay
// st: SessionStage
// dw: DataWorker status
// of: Offline faulted
// os: Entire state of offline
type Health = {
    rc?: number;
    ls?: number;
    ms?: number;
    md?: number;
    lm?: LazyModuleStats[];
    c?: number;
    loaf?: string;
    loafss?: string;
    idloaf?: string;
    idloafss?: string;
    inp?: number;
    id?: number;
    st: string;
    dw: string;
    of: string;
    os: string;
};

export const defaultDatapointTimeout: number = 60 * 1000; // 60 seconds

const possibleStatus = Object.values(DatapointStatus);

let datapointLayoutScore: number = -1;
export function setDatapointLayoutScore(score: number): void {
    datapointLayoutScore = score;
}

let totalReactionCount = 0;
export function incrementReactionCount(): void {
    totalReactionCount++;
}

export class PerformanceDatapoint extends AriaDatapoint implements PerformanceDatapointType {
    public static EventTimeToDpMapping: Map<number, PerformanceDatapoint> = new Map();
    
    private startTime: number | undefined;
    private perfStartTime: number | undefined;
    private startMemory: number | undefined = 0;
    private startScore: number;
    private startReactions: number;
    private timeBeforePause: number = 0;
    private previousEndCalls: string[] = [];
    private timeoutId: number | undefined;
    private invalidated: boolean = false;
    private endTime: number | undefined;
    
    private scenarioEventTimeoutId: number | undefined;
    private lazyLogArugments: {
        calculatedStatus: DatapointStatus;
        errorType: ErrorType | "general";
    } | undefined;
    
    private noMarking: boolean;
    private health: Health;
    private pendingCallbacks: Array<() => void> = [];
    private isEndPending: boolean = false;
    // Elapsed duration (ms from start) captured the first time the datapoint's end
    // is marked pending. Used as E2ETimeElapsed when a pending datapoint is ended
    // via the timeout path so we report the time work actually became pending
    // rather than the full timeout window.
    private endPendingDuration: number | undefined = undefined;
    private endProfile: EndProfileFunction | null = null;
    waterfallTimings: {
        [key: string]: number;
    } | undefined;
    duration: number | undefined;
    allRequestIds: string[] = [];
    responseCorrelationVectors: string[] = [];
    madeNetworkRequest = false;
    didExecuteGqlQuery = false;
    dataSource: DataSource | null = null;
    dataRetries: number | undefined;
    greyError?: Error;
    // Allows to check if a datapoint is a PerformanceDatapoint without
    // having to import "owa-analytics" to check with "instance of"
    isPerformanceDatapoint = true;
    constructor(eventName: AnalyticsEventNames | AnalyticsCoreEventNames, mailbox: MailboxInfo | typeof INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT | undefined, options?: DatapointOptionsForPerformanceDatapoint) {
        super(eventName, mailbox, undefined, options);
        
        this.noMarking = !!options?.noMarking;
        const importTime = getGlobalImportStartTime();
        this.startTime = options?.customStartTime || importTime || Date.now();
        this.startScore = datapointLayoutScore;
        if (!this.options?.skipNonMetadataTasks) {
            this.performanceMark("s");
            
            const eventTimestamp = options?.eventTimestamp;
            if (eventTimestamp) {
                this.setEventTimestamp(eventTimestamp);
            }
        }
        
        if (importTime) {
            this.addData("BundleTime", Date.now() - importTime);
        }
        
        this.startMemory = (self?.performance as PerformanceWithMemory)?.memory?.usedJSHeapSize;
        this.startReactions = totalReactionCount;
        this.perfStartTime = self?.performance?.now();
        this.health = {
            st: getSessionStage(),
            dw: getDataWorkerState(),
            of: getOfflineFaultState(),
            os: getOfflineFaultOrDisabledState(),
        };
        
        if (!this.options?.skipNonMetadataTasks) {
            // if the the datapoint doesn't end in 60 seconds, then end it with a timeout error
            this.timeoutId = setTimeout(this.endWithTimeout.bind(this) as TimerHandler, options && typeof options.timeout == "number"
                ? options.timeout
                : defaultDatapointTimeout);
            
            const optionsCapturePerfProfiles = this.options?.perfProfile?.enableIf === undefined ||
                this.options?.perfProfile?.enableIf;
            const ecsCapturePerfProfiles = getAnalyticsFlightsAndAppSettings(this.getAccountKey())?.capturePerfProfiles;
            if (optionsCapturePerfProfiles &&
                ecsCapturePerfProfiles &&
                ecsCapturePerfProfiles.some(e => e.name == eventName && Date.now() < e.expire)) {
                this.endProfile = startSelfProfiler(eventName, { minDuration: 50 });
            }
        }
        
        addStackTraceToEvent(this.options);
    }
    /**
     * @deprecated Use addToPredefinedWaterfall or addToCustomWaterfall instead. See https://aka.ms/OWALayerCakeGraph for more information.
     */
    addCheckmark(checkmarkName: string, timestamp?: number): number {
        this.performanceMark(checkmarkName);
        const time = this.timeFromStart(timestamp);
        this.addToWaterfall(checkmarkName, time);
        return time;
    }
    /**
     * @deprecated Use addToPredefinedWaterfall or addToCustomWaterfall instead. See https://aka.ms/OWALayerCakeGraph for more information.
     */
    addCheckpoint(checkpoint: string): void {
        this.addToWaterfall(checkpoint, 1);
    }
    /**
     * @deprecated Use addToPredefinedWaterfall or addToCustomWaterfall instead. See https://aka.ms/OWALayerCakeGraph for more information.
     */
    private addToWaterfall(key: string, time: number) {
        if (!this.waterfallTimings) {
            this.waterfallTimings = {};
        }
        try {
            const uniqueKey = getUniquePropertyString(this.waterfallTimings, key, this.eventName);
            if (uniqueKey && !this.hasEnded) {
                this.waterfallTimings[uniqueKey] = time;
            }
        }
        catch {
            // We add checkmarks automatically within the action framework and there can be a lot
            // with the same exact name. So if we can't find a unique property name, don't add the
            // checkmark
        }
    }
    
    addToPredefinedWaterfall(checkpoint: PredefinedCheckpoint, discardIfDefined?: boolean, customTime?: number, actionName?: string): void {
        if (discardIfDefined && this.isWaterfallCheckpointDefined(checkpoint)) {
            trace.warn(`Waterfall Timing Discarded. DP: ${this.eventName}. Checkpoint: ${checkpoint}`);
            return;
        }
        
        const time = customTime === 0 ? 0 : customTime || this.timeFromStart();
        this.addWaterfallColumn(checkpoint, time.toFixed(), actionName);
        if (!customTime && customTime !== 0) {
            this.performanceMark(getWaterfallColumnName(checkpoint));
        }
    }
    
    getWaterfallColumns(): string {
        return Object.entries(this.getProperties())
            .filter(a => a[0].startsWith(WATERFALL_COLUMN_PREFIX))
            .map(([k, v]) => `${k}|${v}`)
            .join(",");
    }
    
    addToCustomWaterfall(index: CustomWaterfallRange, checkpoint: string, discardIfDefined?: boolean, customValue?: number | string): void {
        if (discardIfDefined && this.isWaterfallCheckpointDefined(index)) {
            trace.warn(`Waterfall Timing Discarded. DP: ${this.eventName}. Index: ${index}`);
            return;
        }
        
        const time = customValue === 0 ? 0 : customValue || this.timeFromStart();
        const value = getCustomValue(checkpoint, time);
        
        if (index < 1 || index > 15) {
            logGreyError("CustomWaterfallIndexOutOfRange", anonymousMailbox, undefined, {
                dynamicEventName: `Custom waterfall (${checkpoint}) index must be between 1 and 15. Datapoint name: ${this.eventName}`,
            });
        }
        else {
            this.addWaterfallColumn(getCustomColumn(index), value);
            if (!customValue && customValue !== 0) {
                this.performanceMark(checkpoint);
            }
        }
    }
    
    addToPredefinedOrCustomWaterfall(checkpoint: PredefinedCheckpoint, index: CustomWaterfallRange | undefined, discardIfDefined?: boolean): void {
        if (this.getData(getWaterfallColumnName(checkpoint))) {
            if (!index) {
                logGreyError("CustomCheckpointColumnIsAreadyUsed", anonymousMailbox, 
                /* eslint-disable-next-line owa-custom-rules/no-error-dynamic-event-names -- (https://aka.ms/OWALintWiki)
                 * Error constructor names can only be a string literals.
                 *	> Error constructor names can only be a string literals. Use the diagnosticInfo to add custom data. */
                new Error(`addToPredefinedOrCustomWaterfall for the checkpoint ${checkpoint} (DP: ${this.eventName}) requires a custom index`));
                return;
            }
            
            this.addToCustomWaterfall(index, checkpoint, discardIfDefined);
        }
        else {
            this.addToPredefinedWaterfall(checkpoint, discardIfDefined);
        }
    }
    
    setEventTimestamp(eventTimestamp: number): void {
        this.options.eventTimestamp = eventTimestamp;
        PerformanceDatapoint.EventTimeToDpMapping.set(eventTimestamp, this);
    }
    
    isWaterfallCheckpointDefined(checkpointOrIndex: PredefinedCheckpoint | CustomWaterfallRange): boolean {
        return !!this.getWaterfallColumnData(checkpointOrIndex);
    }
    
    setEndPending(): void {
        if (!this.hasEnded) {
            // Capture the elapsed time at the moment the end first becomes pending so we
            // can report it as E2ETimeElapsed if the datapoint is later ended via timeout.
            if (this.endPendingDuration === undefined) {
                this.endPendingDuration = this.calculateTotalDuration();
            }
            this.schedulePendingCallbackResolutionOnNextPaint(true /* isSetEndPending */);
            this.isEndPending = true;
        }
    }
    
    /**
     * Add a callback to be executed either:
     * - When the next paint occurs or,
     * - When the datapoint ends, or
     * - When resolvePendingCallbacks is called
     */
    addCallbackResolvedAfterNextPaint(cb: () => void): void {
        if (!this.hasEnded) {
            this.schedulePendingCallbackResolutionOnNextPaint();
            this.pendingCallbacks.push(cb);
        }
    }
    
    resolvePendingCallbacks(isVisible?: boolean): void {
        if (!this.hasEnded) {
            this.resolvePendingCallbacksInternal(false /* isEnding */, isVisible);
        }
    }
    
    private schedulePendingCallbackResolutionOnNextPaint(isSetEndPending?: boolean) {
        // Only schedule resolve once per datapoint
        if (this.isEndPending == false && this.pendingCallbacks.length == 0) {
            // if the caller is setEndPending, mark isEndPending as true to call the endAction inside resolvePendingCallbacksInternal
            this.isEndPending = isSetEndPending ?? false;
            getNextPaint((isVisible: boolean) => this.resolvePendingCallbacks(isVisible));
        }
    }
    
    /**
     * Execute all callbacks and end the datapoint if isEndPending is true
     *
     * @param isEnding True when the datapoint started the end process (but has not completed it yet)
     * @param isVisible True if the app is visible; false otherwise
     */
    private resolvePendingCallbacksInternal(isEnding: boolean, isVisible?: boolean) {
        for (const cb of this.pendingCallbacks) {
            cb();
        }
        
        this.pendingCallbacks = [];
        
        if (!isEnding && this.isEndPending) {
            this.endAction(isVisible === false ? DatapointStatus.BackgroundSuccess : DatapointStatus.Success);
        }
    }
    
    private addWaterfallColumn(name: `Custom${CustomWaterfallRange}` | PredefinedCheckpoint, value: string | number, actionName?: string) {
        const key = getWaterfallColumnName(name);
        if (this.getData(key) && !this.dataRetries) {
            // This is a rate signal spread over a small, stable set of datapoint/checkpoint pairs
            // (long-lived datapoints re-adding Network_S/Network_E per request), so logging every
            // occurrence is pure cost. Sample by session to keep each sampled session's shape intact.
            logGreyError("WaterfallCheckpointAlreadyExists", anonymousMailbox, undefined, {
                dynamicEventName: `Waterfall checkpoint ${name} already exists. It can only be logged once. Name: ${this.eventName} ActionName: ${actionName}`,
            }, { sessionSampleRate: 5 });
        }
        else {
            this.addData(key, value);
        }
    }
    
    endWithError(status: DatapointStatus | ((duration?: number) => DatapointStatus), error: ErrorCodes | TraceErrorObject, duration?: number, errorType?: ErrorType): Promise<void> {
        const resolvedStatus = typeof status == "function" ? status(duration) : status;
        // In some cases, Typescript doesn't catch that an error is being passed instead of a DatapointStatus
        // for the status arguments. We manually check that it's a valid input at runtime.
        if (!possibleStatus.includes(resolvedStatus)) {
            const errorString = `Invalid call to "endWithError". Expected status to be of a "DatapointStatus" type but got ${status} for the datapoint named ${this.eventName}`;
            trace.warn(errorString);
            logUsage("InvalidStatusForEndWithError", anonymousMailbox, {
                message: errorString,
                datapoint: this.eventName,
            });
        }
        
        // Check if error is an ErrorCode (string) or an Error-like object
        if (typeof error === "string") {
            // If it's a string, treat it as ErrorCodes (format like "00_0000", "01_0001", etc.)
            return this.endInternal("endWithError", duration, resolvedStatus, undefined, errorType, error as ErrorCodes);
        }
        else {
            const errorCodeAndStatus: ErrorCodeAndStatus = getUnhandledErrorCode(error as TraceErrorObject, resolvedStatus, errorType);
            
            return this.endInternal("endWithError", duration, errorCodeAndStatus.status, error as TraceErrorObject, errorCodeAndStatus.errorType, errorCodeAndStatus.errorCode);
        }
    }
    
    /**
     * End the datapoint with a specific error status when the failure type is known.
     * Use this method when you have already determined the appropriate DatapointStatus.
     */
    endWithErrorAndDetermineStatus(status: DatapointStatus | ((duration?: number) => DatapointStatus), error: ErrorCodes | TraceErrorObject, duration?: number, errorType?: ErrorType): Promise<void> {
        return this.endWithError(status, error, duration, errorType);
    }
    
    /**
     * End the datapoint with automatic error classification when the failure type is unknown.
     * This method performs error analysis to determine the most appropriate
     * DatapointStatus and error categorization. It starts with ClientError as the default
     * but automatically overrides to more specific statuses (ServerError, NetworkError,
     * AuthError, etc.) based on error analysis of HTTP codes, response patterns, and
     * error characteristics.
     */
    endWithUnhandledError(error: TraceErrorObject, duration?: number, errorType?: ErrorType): Promise<void> {
        return this.endWithError(DatapointStatus.ClientError, error, duration, errorType);
    }
    
    /**
     * End the datapoint with server response error details.
     * Automatically creates an Error object with relevant properties from the response.
     */
    endWithServerResponseError(status: DatapointStatus, response: Response | {
        ResponseClass?: string;
        MessageText?: string;
        ResponseCode?: string;
    }
    //ErrorResponseB2
     | {
        code?: number;
        message?: string;
        error?: string;
    } | {
        status: number;
        statusText?: string;
    } | undefined, duration?: number, errorType?: ErrorType): Promise<void> {
        const error = new Error("Server response indicated failure") as TraceErrorObject;
        
        if (isFetchApiResponse(response)) {
            // Standard Fetch API Response
            error.additionalInfo = {
                httpStatusText: response.statusText,
                responseUrl: response.url || undefined,
                responseType: "fetch",
                eventName: this.eventName,
            };
            error.httpStatus = response.status;
        }
        else if (isSingleResponseMessage(response)) {
            // OWS service response object (SingleResponseMessage or derived types)
            error.additionalInfo = {
                responseClass: response.ResponseClass,
                messageText: response.MessageText,
                responseType: "ows",
                eventName: this.eventName,
            };
            error.responseCode = response.ResponseCode;
        }
        else if (response &&
            typeof response === "object" &&
            ("code" in response || "message" in response || "error" in response)) {
            // Error response object with code/message/error properties. [ErrorResponseB2]
            error.additionalInfo = {
                code: response.code,
                message: response.message,
                error: response.error,
                responseType: "ErrorResponseB2",
                eventName: this.eventName,
            };
        }
        else {
            // Unknown response type - log what we can determine
            error.additionalInfo = {
                responseType: "unknown",
                eventName: this.eventName,
                responseTypeof: typeof response,
                hasStatusProperty: "status" in (response || {}),
                hasResponseClassProperty: "ResponseClass" in (response || {}),
            };
        }
        
        return this.endWithError(status, error, duration, errorType);
    }
    
    endAfterAnimationFrame(): void {
        safeRequestAnimationFrame(() => {
            this.endInternal("endAfterAnimationFrame");
        });
    }
    end(duration?: number, overrideStatus?: DatapointStatus | ((duration?: number) => DatapointStatus), errorIn?: TraceErrorObject, errorTypeIn?: ErrorType | "general"): void {
        this.endInternal("end", duration, overrideStatus, errorIn, errorTypeIn);
    }
    markUserPerceivedTime(waitForAnimationFrame?: boolean, overrideTime: boolean = true): void {
        if (!overrideTime && this.getData("UserPerceivedTime")) {
            return;
        }
        if (waitForAnimationFrame) {
            safeRequestAnimationFrame(this.addUserPerceivedTime.bind(this));
        }
        else {
            this.addUserPerceivedTime();
        }
    }
    invalidate(): void {
        this.disposeandMarkEnded();
        this.invalidated = true;
    }
    pause(): void {
        this.timeBeforePause += this.timeFromStart();
        this.startTime = undefined;
    }
    resume(): void {
        if (!this.startTime) {
            this.startTime = Date.now();
        }
    }
    endAction(overrideStatus?: DatapointStatus, error?: TraceErrorObject): void {
        // The scneario might have ended the datapoint with returnTopExecutingAction.
        // If it has then we don't need to the middleware to end the datapoint.
        if (!this.hasEnded) {
            this.invalidated = true;
            this.addDataWithoutChecks("RequestIds", this.allRequestIds.join(";"));
            this.addDataWithoutChecks("cV", this.responseCorrelationVectors.join(";"));
            this.addDataWithoutChecks("Cache", this.madeNetworkRequest ? "NoCache" : "Cache");
            
            const errorCodeAndStatus: ErrorCodeAndStatus = getUnhandledErrorCode(error as TraceErrorObject, overrideStatus ?? DatapointStatus.ClientError);
            
            this.endInternal("endAction", undefined, errorCodeAndStatus.status, error, errorCodeAndStatus.errorType, errorCodeAndStatus.errorCode);
        }
    }
    addDataSource(dataSource: DataSource): void {
        this.dataSource = dataSource;
        
        if (isNetworkDataSource(dataSource)) {
            this.madeNetworkRequest = true;
        }
    }
    getWaterfallColumnData(checkpointOrIndex: PredefinedCheckpoint | CustomWaterfallRange): CustomDataType {
        return this.getData(getWaterfallColumnName(typeof checkpointOrIndex === "number"
            ? getCustomColumn(checkpointOrIndex)
            : checkpointOrIndex));
    }
    getDataSource(): DataSource | null {
        return this.dataSource;
    }
    getStartTime(): number | undefined {
        return this.startTime;
    }
    calculateTotalDuration(duration?: number): number {
        return typeof duration === "number"
            ? Math.floor(duration)
            : this.timeFromStart() + this.timeBeforePause;
    }
    getE2ETimeElapsed() {
        return this.getData("E2ETimeElapsed") as number | undefined;
    }
    isSent(): boolean {
        return this.hasEnded;
    }
    
    rehydrateFromJSObject(jsObject: PerformanceDatapointObjectType, normalizeWaterfallStart?: boolean): void {
        for (const requestId of jsObject.allRequestIds ?? []) {
            if (!this.allRequestIds.includes(requestId)) {
                this.allRequestIds.push(requestId);
            }
        }
        
        if (jsObject.dataSource) {
            this.addDataSource(jsObject.dataSource);
        }
        
        if (jsObject.properties) {
            for (let [key, value] of Object.entries(jsObject.properties) as [
                string,
                string
            ][]) {
                if (value && key?.startsWith("WF_") && !this.getData(key)) {
                    if (normalizeWaterfallStart) {
                        const dpStart = this.getStartTime();
                        const jsObjectStart = jsObject.startTime;
                        
                        if (dpStart && jsObjectStart) {
                            const [name, offset] = value.split("|") as [
                                string,
                                string
                            ];
                            value = `${name}|${jsObjectStart - dpStart + Number(offset)}`;
                        }
                    }
                    
                    this.getProperties()[key] = value;
                }
            }
        }
        
        if (jsObject.propertyBag) {
            for (const key of Object.keys(jsObject.propertyBag)) {
                this.addCustomProperty(key, jsObject.propertyBag[key]);
            }
        }
        
        this.didExecuteGqlQuery = !!(this.didExecuteGqlQuery || jsObject.didExecuteGqlQuery);
    }
    
    toJSObject() {
        return JSON.parse(JSON.stringify(this)) as PerformanceDatapointObjectType;
    }
    
    public static fromPerfDpJSObject(datapointObj: PerformanceDatapointObjectType): PerformanceDatapoint {
        // Copy options, force skipNonMetadataTasks, and strip mailbox from options bag.
        const options = { ...(datapointObj.options || {}), skipNonMetadataTasks: true } as any;
        delete options.mailbox;
        
        const dp = Object.assign(new PerformanceDatapoint(datapointObj.eventName as AnalyticsEventNames | AnalyticsCoreEventNames, datapointObj.mailbox, options), datapointObj) as PerformanceDatapoint;
        
        // Object.assign overwrites ALL fields from the serialized object, including
        // non-serializable ones (functions, callback arrays) that were corrupted by
        // JSON round-tripping or structured clone. Restore them to safe defaults.
        dp.pendingCallbacks = [];
        dp.endProfile = null;
        
        // Reset stale timer handles copied from the serialized object so
        // disposeAndMarkEnded does not clearTimeout on IDs from the old context.
        dp.timeoutId = undefined;
        dp.scenarioEventTimeoutId = undefined;
        
        return dp;
    }
    
    private async endInternal(type: "end" | "endWithError" | "endAfterAnimationFrame" | "endAction" | "endWithTimeout", duration?: number, overrideStatus?: DatapointStatus | ((duration?: number) => DatapointStatus), errorIn?: TraceErrorObject, errorTypeIn?: ErrorType | "general", errorCode?: ErrorCodes) {
        if (this.hasEnded) {
            if (!this.invalidated) {
                logUsage("ActionCalledAfterPerfDatapointEndedV2", anonymousMailbox, {
                    event: this.eventName,
                    type,
                    endCalls: this.previousEndCalls.join(","),
                    errMsg: scrubForPii(errorIn?.message),
                    errorTypeIn,
                });
            }
        }
        else {
            /**
             * The duration should always be the first calculation of this function.
             */
            
            // if duration is not passed in, then calculate the time based of the current time it was ended and the time the datapoint was created.
            const totalDuration = this.calculateTotalDuration(duration);
            // if the duration was measured elsewhere then we really don't know when the measured interval ended
            if (duration === undefined) {
                this.performanceMark("e");
            }
            this.addDataWithoutChecks("E2ETimeElapsed", totalDuration);
            
            /**
             * Other checks and calculations can be added here
             */
            
            const shouldLogPerfProfile = this.options?.perfProfile?.discardIf === undefined ||
                this.options?.perfProfile?.discardIf?.(this);
            this.endProfile?.(shouldLogPerfProfile);
            this.endProfile = null;
            
            const error = tryGetTraceObjectErrorFromApolloError(errorIn);
            const statusAndType = getStatusAndType(this, error);
            
            const calculatedStatus = (typeof overrideStatus == "function" ? overrideStatus(duration) : overrideStatus) ||
                statusAndType?.status ||
                DatapointStatus.Success;
            const errorType = errorTypeIn || statusAndType?.type || "general";
            
            if (calculatedStatus == DatapointStatus.AuthError) {
                const isSigninRequired = getIsSigninRequiredForTelemetry();
                if (isSigninRequired !== undefined) {
                    this.addCustomProperty("isSigninRequired_DatapointEnd", isSigninRequired);
                }
            }
            
            this.disposeandMarkEnded();
            this.endTime = typeof performance.now === "function" ? performance.now() : undefined;
            
            if (this.dataSource) {
                this.addDataWithoutChecks("DataSource", this.dataSource);
            }
            
            if (this.dataRetries) {
                this.addDataWithoutChecks("DataRetries", this.dataRetries);
            }
            
            this.addHealth();
            
            // Capture isEndPending when we fail
            if (calculatedStatus != DatapointStatus.Success) {
                this.options.customDiagnosticsOptions = { isEndPending: this.isEndPending };
            }
            
            const errorCodeToLog = errorCode || errorIn?.owaErrorCode;
            if (errorCodeToLog && validateErrorCode(errorCodeToLog)) {
                addEncounteredErrorCode(errorCodeToLog, this.eventName);
                this.addDataWithoutChecks("ErrorCode", errorCodeToLog);
            }
            
            if (error) {
                trace.warn(`Datapoint ${this.eventName} ended with an error (Code: ${errorCodeToLog || "NoCodeFound"}) of ${error.message}`);
            }
            
            // if we have a scenario event, then wait for the correlated performance observer to fire
            if (this.options?.eventTimestamp) {
                // save the values in case we log it later after the scenario event comes in
                this.lazyLogArugments = { calculatedStatus, errorType };
                
                // if the scenario event doesn't come in after 5 seconds then log it anyways
                this.scenarioEventTimeoutId = self.setTimeout(() => {
                    this.logEvent(calculatedStatus, errorType);
                    this.lazyLogArugments = undefined;
                }, 5000);
            }
            else {
                this.logEvent(calculatedStatus, errorType);
            }
        }
        this.previousEndCalls.push(type);
        
        // Enqueue into the rolling datapoint cache for ODS (if the addon is registered)
        const datapointsAddon = getAnalyticsAddon("DatapointsCachePush");
        if (datapointsAddon?.isRegistered) {
            datapointsAddon.executeWhenReady(this.eventName, this.getAllProperties().Status, this.getAllProperties().E2ETimeElapsed);
        }
    }
    /**
     * Cleans up any resources held by the datapoint.
     */
    private disposeandMarkEnded() {
        // resolve any pending waterfall marks before we end
        this.resolvePendingCallbacksInternal(true /* isEnding */);
        
        this.hasEnded = true;
        
        // clear any timeouts
        clearTimeout(this.timeoutId);
        clearTimeout(this.scenarioEventTimeoutId);
        
        // Stop (and discard) any running self profiler. Dropping the reference without calling
        // it leaves the sampling profiler running and its module level isRunning flag set, which
        // blocks self profiling for the rest of the session. Passing false discards the trace so
        // no event is logged for a datapoint that is being thrown away.
        this.endProfile?.(false);
        this.endProfile = null;
        this.lazyLogArugments = undefined;
    }
    
    private addHealth() {
        let errorStep: string = "s";
        try {
            const endTime: number | undefined = self?.performance?.now();
            
            errorStep = "rc";
            if (this.startScore > -1) {
                this.health.rc = totalReactionCount - this.startReactions;
            }
            
            errorStep = "c";
            if (this.startScore > -1) {
                this.health.ls = datapointLayoutScore - this.startScore;
            }
            
            errorStep = "m";
            if (this.startMemory) {
                if (this.startMemory > 0) {
                    this.health.ms = Math.floor(this.startMemory / 1024); // report in KB
                }
                
                const current = (self?.performance as PerformanceWithMemory)?.memory
                    ?.usedJSHeapSize;
                const difference = Math.floor((current - this.startMemory) / 1024);
                if (difference > 0) {
                    this.health.md = difference; // report in KB
                }
            }
            
            errorStep = "lm";
            const lazyLoadedModules = getLazyLoadedModulesAndCleanup(this.perfStartTime);
            if (lazyLoadedModules.length > 0) {
                const lazyModules = this.matchLazyLoadedModules(lazyLoadedModules, endTime);
                if (lazyModules.length > 0) {
                    this.health.lm = lazyModules;
                }
            }
            
            errorStep = "hc";
            const hardwareConcurrency = self.navigator?.hardwareConcurrency;
            if (typeof hardwareConcurrency == "number") {
                this.health.c = hardwareConcurrency;
            }
        }
        catch (e) {
            logUsage("DatapointHealthError", anonymousMailbox, {
                error: e instanceof Error ? e.message : String(e),
                errorStep,
            });
        }
    }
    private matchLazyLoadedModules(lazyLoadedModules: LazyLoadedModules[], end: number): LazyModuleStats[] {
        const match: LazyModuleStats[] = [];
        
        for (const { start, duration, attemps } of lazyLoadedModules) {
            if (this.perfStartTime && start >= this.perfStartTime && start + duration <= end) {
                const moduleStats: LazyModuleStats = {
                    d: Math.floor(duration),
                };
                if (attemps > 1) {
                    moduleStats.a = attemps;
                }
                match.push(moduleStats);
            }
        }
        
        return match;
    }
    private addUserPerceivedTime(): void {
        this.performanceMark("UserPerceivedTime");
        this.addData("UserPerceivedTime", this.timeFromStart());
    }
    private performanceMark(value: string) {
        if (!this.noMarking) {
            addPerfomanceMarkToDatapoint(this, value);
        }
    }
    private endWithTimeout() {
        if (this.startTime && hasUserWindowHadFocusSince(this.startTime)) {
            // end with timeout is a race condition so it is possible that it ends multiple times
            this.invalidated = true;
            this.options = this.options || {};
            this.options.logVerbose = true;
            // If the datapoint's end is pending (e.g. it is finishing work in the background),
            // treat the timeout as a background success rather than a timeout failure.
            // For the background-success path, report E2ETimeElapsed as the time the end
            // became pending rather than the full timeout window.
            this.endInternal("endWithTimeout", this.isEndPending ? this.endPendingDuration : undefined, this.isEndPending ? DatapointStatus.BackgroundSuccess : DatapointStatus.Timeout);
        }
        else if (this.startTime) {
            // The window was hidden at some point after this datapoint started, so a foreground
            // timeout failure would be misleading and we deliberately log nothing here.
            //
            // This timer is one-shot and the focus gate cannot recover for this datapoint:
            // hiding the document advances hasHadFocusSince, and every later visible transition
            // sets it to a time after startTime, so hasUserWindowHadFocusSince(startTime) stays
            // false forever. Re-arming the timer would therefore never finalize the datapoint.
            // Without a terminal action it would stay live for the rest of the session, holding
            // its pending callbacks and self profiler, so discard it instead. invalidate() marks
            // it ended, which guarantees it can never emit an event afterwards.
            this.invalidate();
        }
        // A paused datapoint (startTime === undefined) is intentionally left alone: resume()
        // can restore it and it is still expected to end normally.
    }
    
    private logEvent(calculatedStatus: DatapointStatus, errorType: ErrorType | "general") {
        if (this.options?.eventTimestamp && this.endTime) {
            this.addDataWithoutChecks("TrueDuration", Math.floor(this.endTime - this.options.eventTimestamp));
        }
        if (Object.keys(this.health).length > 0) {
            this.addDataWithoutChecks("Health", JSON.stringify(this.health));
        }
        lazyLogPerformanceDatapoint.importAndExecute(this, calculatedStatus, errorType);
    }
    timeFromStart(timestamp?: number): number {
        return this.startTime ? (timestamp || Date.now()) - this.startTime : 0;
    }
    
    addScenarioEventTiming(entry: PerformanceEventTiming, eventLocations: string[], eventSources: string[], inputDelayLocations: string[], inputDelaySources: string[]): void {
        self.clearTimeout(this.scenarioEventTimeoutId);
        
        this.health.loaf = eventLocations.join("|");
        this.health.loafss = eventSources.join("|");
        this.health.idloaf = inputDelayLocations.join("|");
        this.health.idloafss = inputDelaySources.join("|");
        this.health.inp = Math.floor(entry.duration);
        this.health.id = Math.floor(entry.processingStart - entry.startTime);
        if (this.lazyLogArugments) {
            const { calculatedStatus, errorType } = this.lazyLogArugments;
            this.logEvent(calculatedStatus, errorType);
        }
        else {
            // make sure to unset this so we log the datapoint if end is called afterwards
            this.options.eventTimestamp = undefined;
        }
    }
}

function getStatusAndType(datapoint: PerformanceDatapointType, error: TraceErrorObject | undefined): StatusAndType | undefined {
    if (error) {
        const calculatedErrorMessage = addErrorToDatapoint(datapoint, error);
        return getDatapointStatus(calculatedErrorMessage, error);
    }
    return undefined;
}
